import React from "react";
import ReactMarkdown from "react-markdown";
import { Typography, Box } from "@mui/material";
import MarkdownCode from "../Common/MarkdownCode";
import color from "../../Constants/colors";

interface MarkdownRendererProps {
  content: string;
  messageRole: "user" | "assistant" | "system" | "tool";
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  messageRole,
}) => {
  return (
    <ReactMarkdown
      components={{
        h1: ({ ...props }) => (
          <Typography
            variant="subtitle1"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{
              fontWeight: "bold",
              mt: 1.5,
              mb: 0.5,
            }}
            {...props}
          />
        ),
        h2: ({ ...props }) => (
          <Typography
            variant="subtitle2"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{
              fontWeight: "bold",
              mt: 1.5,
              mb: 0.5,
            }}
            {...props}
          />
        ),
        h3: ({ ...props }) => (
          <Typography
            variant="caption"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              mt: 1,
              mb: 0.25,
              display: "block",
            }}
            {...props}
          />
        ),
        h4: ({ ...props }) => (
          <Typography
            variant="caption"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{
              fontWeight: "bold",
              fontSize: "0.75rem",
              mt: 0.75,
              mb: 0.25,
              display: "block",
            }}
            {...props}
          />
        ),
        hr: () => (
          <Box
            component="hr"
            sx={{
              border: "none",
              borderTop: `1px solid ${
                messageRole === "user" ? "rgba(255,255,255,0.3)" : color.gray300
              }`,
              my: 1,
            }}
          />
        ),
        ul: ({ ordered, ...props }) => (
          <Box
            component="ul"
            sx={{
              pl: 2,
              my: 0.5,
            }}
            {...props}
          />
        ),
        ol: ({ ordered, ...props }) => (
          <Box
            component="ol"
            sx={{
              pl: 2,
              my: 0.5,
            }}
            {...props}
          />
        ),
        a: ({ ...props }) => (
          <Typography
            component="a"
            variant="caption"
            sx={{
              color: messageRole === "user" ? color.accent : color.primary_dark,
              textDecoration: "underline",
              wordBreak: "break-all",
            }}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          />
        ),
        blockquote: ({ ...props }) => (
          <Box
            component="blockquote"
            sx={{
              borderLeft: `3px solid ${
                messageRole === "user" ? "rgba(255,255,255,0.5)" : color.gray400
              }`,
              pl: 1.5,
              my: 0.5,
              ml: 0,
              opacity: 0.85,
            }}
            {...props}
          />
        ),
        table: ({ ...props }) => (
          <Box
            component="table"
            sx={{
              borderCollapse: "collapse",
              width: "100%",
              my: 1,
              "& th, & td": {
                border: `1px solid ${
                  messageRole === "user"
                    ? "rgba(255,255,255,0.3)"
                    : color.gray300
                }`,
                px: 1,
                py: 0.5,
                fontSize: "0.75rem",
                color: messageRole === "user" ? "white" : color.primary_dark,
              },
              "& th": {
                fontWeight: "bold",
                backgroundColor:
                  messageRole === "user"
                    ? "rgba(255,255,255,0.1)"
                    : color.gray200,
              },
            }}
            {...props}
          />
        ),
        p: ({ ...props }) => (
          <Typography
            variant="caption"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{
              wordWrap: "break-word",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              padding: {
                xs: "0.25rem",
                md: "0rem",
                xl: "0rem",
              },
            }}
            {...props}
          />
        ),
        strong: ({ ...props }) => (
          <Typography
            component="strong"
            variant="caption"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{ fontWeight: "bold" }}
            {...props}
          />
        ),
        em: ({ ...props }) => (
          <Typography
            component="em"
            variant="caption"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{ fontStyle: "italic" }}
            {...props}
          />
        ),
        li: ({ ordered, ...props }) => (
          <Typography
            component="li"
            variant="caption"
            color={messageRole === "user" ? "white" : color.primary_dark}
            sx={{
              wordWrap: "break-word",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              padding: {
                xs: "0.25rem",
                md: "0rem",
                xl: "0rem",
              },
              lineHeight: "16px",
            }}
            {...props}
          />
        ),
        pre: ({ ...props }) => (
          <Box
            component="pre"
            sx={{
              width: "100%",
            }}
            {...props}
          />
        ),
        code: (props) => <MarkdownCode variant="chatUi" {...props} />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
